import PropTypes from "prop-types";

/**
 * Bold social icon button component with modern styling.
 * Follows UI/UX best practices with:
 * - Consistent 56px sizing for better touch targets
 * - Bold, filled icons for strong visual presence
 * - Smooth animations (scale + color transitions)
 * - Dark mode support with high contrast
 * - Full keyboard accessibility
 * - Enhanced hover/focus states
 */
const icons = {
  instagram: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full">
      <rect x="2" y="2" width="20" height="20" rx="4" ry="4" fill="none" stroke="currentColor" strokeWidth="2" />
      <circle cx="12" cy="12" r="4" fill="none" stroke="currentColor" strokeWidth="2" />
      <circle cx="17.5" cy="6.5" r="1.5" fill="currentColor" />
    </svg>
  ),
  linkedin: (
    <svg viewBox="0 0 24 24" fill="white">
      <rect x="2" y="2" width="20" height="20" rx="2" fill="white" />
      <circle cx="7" cy="8.5" r="1.2" fill="#2A3D21" />
      <rect x="6" y="10" width="2" height="7" fill="#2A3D21" />
      <path d="M15 10c1.5 0 2.5 1 2.5 2.5V17h-2v-4c0-1-0.5-1.5-1.5-1.5s-1.5 0.5-1.5 1.5v4h-2v-7h2v1c0.5-0.8 1.5-1.3 2.5-1.3z" fill="#2A3D21" />
    </svg>
  ),
  github: (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C6.48 2 2 6.48 2 12c0 4.42 2.865 8.17 6.84 9.49.5.09.685-.22.685-.485 0-.236-.009-.86-.014-1.695-2.782.603-3.369-1.34-3.369-1.34-.454-1.156-1.11-1.463-1.11-1.463-.908-.62.069-.608.069-.608 1.003.07 1.532 1.03 1.532 1.03.89 1.529 2.341 1.547 2.91 1.183.09-.92.348-1.546.635-1.903-2.22-.254-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0112 6.836c.85.004 1.705.113 2.504.334 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C19.138 20.161 22 16.418 22 12c0-5.52-4.48-10-10-10z" />
    </svg>
  ),
  email: (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
    </svg>
  ),
  phone: (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.92 7.02C17.45 6.18 16.51 5.55 15.46 5.55c-2.64 0-4.84 3.06-4.84 6.8 0 .38.03.74.09 1.08.41 2.53 1.66 4.98 3.5 6.53 1.24 1.04 2.79 1.64 4.47 1.64.65 0 1.29-.1 1.9-.3 1.1-.35 2.1-1.04 2.84-1.99l-1.48-1.3c-.5.6-1.2 1.08-2.02 1.31-.39.12-.82.17-1.24.17-1.16 0-2.27-.39-3.15-1.07-1.27-.96-2.26-2.68-2.62-4.65-.1-.52-.14-1.07-.14-1.65 0-1.57.64-3.01 1.69-4.04.55-.52 1.23-.92 1.98-1.14.4-.12.83-.18 1.27-.18.52 0 1.04.08 1.53.25.97.31 1.85.95 2.48 1.82l1.47-1.42z" />
    </svg>
  ),
};

export default function SocialIconButton({ href, label, icon, target = "_blank", rel = "noopener noreferrer" }) {
  const isMailto = href.startsWith("mailto:");
  const isPhone = href.startsWith("tel:");
  const iconSvg = icons[icon];

  return (
    <a
      href={href}
      target={isMailto || isPhone ? "_self" : target}
      rel={isMailto || isPhone ? undefined : rel}
      className="group relative flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br from-black/5 to-black/10 dark:from-white/10 dark:to-white/20 border border-black/15 dark:border-white/20 text-black dark:text-white shadow-sm hover:shadow-md transition-all duration-300 hover:scale-110 hover:-translate-y-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(var(--app-bg))] focus-visible:ring-black dark:focus-visible:ring-white"
      aria-label={label}
      title={label}
    >
      <div className="w-6 h-6 flex items-center justify-center text-black/80 dark:text-white/90 group-hover:text-black dark:group-hover:text-white transition-colors duration-300">
        {iconSvg}
      </div>
      
      {/* Subtle background glow on hover */}
      <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-black/0 to-black/0 group-hover:from-black/5 group-hover:to-black/15 dark:group-hover:from-white/5 dark:group-hover:to-white/15 transition-all duration-300" />
    </a>
  );
}

SocialIconButton.propTypes = {
  href: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  target: PropTypes.string,
  rel: PropTypes.string,
};
